import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Spin, Table } from 'antd';
import pluralize from 'pluralize';
import cx from 'classnames';

import {
  getMyCustomersDetailExcel,
  getMyCustomersDetail,
} from 'redux/reducers/homeDashboardSlice';
import {
  SummaryCustomerDetailModel,
  SummaryCustomerDetailResponseModel,
} from 'api/backofficeApiClient';
import { downloadFileByObjectURL } from 'utils/fileUtils';
import BaseAlert from 'components/wrappers/BaseAlert/BaseAlert';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import BasePageLabel from 'components/wrappers/BasePageLabel';
import BaseTableHeader from 'components/wrappers/BaseTableHeader';
import { useAppDispatch } from 'hooks/useAppDispatch';

import styles from './MyCustomersDetailsPage.module.less';
import commonStyles from '../UserHomePage.module.less';

const columns = [
  {
    title: 'Customer',
    key: 'customer',
    render: (record: SummaryCustomerDetailModel) => (
      <Link className="wrapped-text" to={`/customer/${record.customerKey}`}>
        {record.customerCompanyName}
      </Link>
    ),
  },
  {
    title: 'Customer ID',
    key: 'customerId',
    render: (record: SummaryCustomerDetailModel) => record.customerKey,
  },
  {
    title: 'Active assignments',
    key: 'activeAssignments',
    render: (record: SummaryCustomerDetailModel) =>
      record.activeAssignmentsCount,
  },
  {
    title: 'Total assignments',
    key: 'totalAssignments',
    render: (record: SummaryCustomerDetailModel) => record.assignmentsCount,
  },
];

export default function MyCustomersDetailsPage() {
  const dispatch = useAppDispatch();
  const [isInitialDataLoaded, setIsInitialDataLoaded] = useState(false);
  const [isCustomersDataLoading, setIsCustomersDataLoading] = useState(false);
  const [customersData, setCustomersData] =
    useState<SummaryCustomerDetailResponseModel>();
  const customersCountMessage = pluralize(
    'customer',
    customersData?.totalCount,
    true,
  );
  const handleDownloadReport = async () => {
    const result = await dispatch(getMyCustomersDetailExcel());

    if (getMyCustomersDetailExcel.fulfilled.match(result)) {
      downloadFileByObjectURL(result.payload.url, result.payload.fileName);
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  const loadCustomersDetails = useCallback(
    async (page: number) => {
      setIsCustomersDataLoading(true);

      const result = await dispatch(getMyCustomersDetail(page - 1));

      if (getMyCustomersDetail.fulfilled.match(result)) {
        setCustomersData(result.payload);
      } else {
        setCustomersData({
          totalCount: 0,
          details: [],
        });
      }

      setIsCustomersDataLoading(false);
    },
    [dispatch],
  );

  const loadInitialData = useCallback(async () => {
    await loadCustomersDetails(1);

    setIsInitialDataLoaded(true);
  }, [loadCustomersDetails]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  return (
    <div className="page-wrapper scroll-box">
      <div className="page-holder">
        <BasePageLabel label="Dashboard details" />

        <div className="page-box page-inner">
          <div className="page-inner-container">
            <BaseTableHeader
              enableSubTitle={isInitialDataLoaded}
              subTitle={customersCountMessage}
              title="My customers"
            >
              <div>
                <Button
                  className={commonStyles.saveBtn}
                  onClick={handleDownloadReport}
                  icon={<span className="icon icon-xls-file" />}
                />
              </div>
            </BaseTableHeader>

            {!isInitialDataLoaded && <Spin />}
            {isInitialDataLoaded && (
              <Table
                className={cx('custom-table', styles.table)}
                pagination={{
                  total: customersData?.totalCount,
                  pageSize: 12,
                  onChange: loadCustomersDetails,
                  showSizeChanger: false,
                }}
                columns={columns}
                dataSource={customersData?.details}
                rowKey={(record) => record?.customerId as string}
                loading={isCustomersDataLoading}
                locale={{
                  emptyText: 'You currently do not have any customers.',
                }}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
